import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { securePost } from '@/utils/secureApi';

import { ASTERPOS_LOGO } from '@/config/brandAssets';
import { boPath } from '@/utils/getBasePath';
import GoogleSignInButton from '@/components/GoogleSignInButton';

export default function OwnerLoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: ''
  });

  // Signed in (password or Google): the session the Back Office reads
  const signedIn = (data) => {
    // Login successful - auth cookie is set by backend
    // Store non-sensitive session data in sessionStorage (NOT localStorage)
    if (data.manager) {
      sessionStorage.setItem('merchant_data', JSON.stringify(data.manager));
    }
    if (data.restaurants) {
      sessionStorage.setItem('merchant_restaurants', JSON.stringify(data.restaurants));
    }

    // BackOfficeLayout authenticates from localStorage.backoffice_token;
    // without it the owner lands on a second login screen.
    if (data.access_token) {
      localStorage.setItem('backoffice_token', data.access_token);
    }
    if (data.primary_restaurant_id) {
      localStorage.setItem('backoffice_active_restaurant', String(data.primary_restaurant_id));
    }
    window.dispatchEvent(new Event('backoffice-login'));

    toast.success('Login successful!');
    navigate(boPath());
  };

  const handleGoogle = async (credential) => {
    try {
      signedIn(await securePost('/merchant/google-login', { credential }));
    } catch (error) {
      toast.error(error.message || 'Google sign-in failed. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const data = await securePost('/merchant/login', {
        email: form.email,
        password: form.password,
      });

      // Check if OTP is required. There is no standalone OTP page; the
      // Back Office login screen owns the full 2FA flow (SMS/TOTP), so
      // send the owner there to finish signing in.
      if (data.requires_otp) {
        toast.info('2FA verification required. Please sign in to finish verification.');
        navigate(boPath());
        return;
      }

      signedIn(data);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Failed to log in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={ASTERPOS_LOGO} alt="Chain29" className="h-8 w-8" />
            <span className="text-xl font-bold text-white">Chain29</span>
          </Link>
          <Link to="/owner/signup">
            <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10">
              Create Account
            </Button>
          </Link>
        </div>
      </div>

      <Card className="w-full max-w-md bg-white/10 backdrop-blur-sm border-white/20 mt-16">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 rounded-full bg-orange-500/20 w-fit">
            <Building2 className="w-8 h-8 text-orange-400" />
          </div>
          <CardTitle className="text-2xl text-white">Restaurant Owner Login</CardTitle>
          <CardDescription className="text-slate-300">
            Access your restaurant dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <GoogleSignInButton onSuccess={handleGoogle} onError={() => toast.error('Google sign-in failed')}
            buttonText="signin_with" className="mb-4" />
          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/20" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-slate-800 px-2 text-slate-400">or with email</span>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="owner@restaurant.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link to={`${boPath()}?forgot=1`} className="text-sm text-orange-400 hover:underline">
                Forgot password?
              </Link>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              disabled={loading}
              data-testid="owner-login-submit"
            >
              {loading ? 'Signing In...' : 'Sign In'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-slate-400 text-sm">
              Don't have an account?{' '}
              <Link to="/owner/signup" className="text-orange-400 hover:underline">
                Start your free trial
              </Link>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-slate-500 text-xs text-center mb-3">Other login options</p>
            <div className="flex gap-2">
              <Link to="/staff/login" className="flex-1">
                <Button variant="outline" className="w-full border-white/20 text-slate-300 hover:bg-white/10 hover:text-white text-xs">
                  Staff / Server Login
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
